import { getAccessToken } from './supabase';
import { apiRequest } from './api';

// WhatsApp Proxy Service URL from environment
const WHATSAPP_PROXY_URL = import.meta.env.VITE_WHATSAPP_SERVICE_URL || 'http://localhost:3001';

/**
 * Build the correct URL based on the path
 * - WhatsApp routes go to proxy service (port 3001)
 * - Template-related endpoints stay in Next.js (port 3000)
 * - All other routes go through Vite proxy to Next.js (port 3000)
 */
function buildUrl(path: string): string {
  // Exception: Template sending goes to Next.js, not WhatsApp proxy
  if (path.startsWith('/api/whatsapp/send-from-template')) {
    return path; // Use Vite proxy to Next.js
  }

  if (path.startsWith('/api/whatsapp')) {
    return `${WHATSAPP_PROXY_URL}${path}`;
  }
  // Return relative path - Vite proxy will handle routing to Next.js
  return path;
}

/**
 * API client wrapper for TanStack Query hooks
 */
export const api = {
  async get<T>(url: string, options?: { params?: Record<string, any> }): Promise<{ data: T }> {
    const token = await getAccessToken();
    const searchParams = options?.params
      ? '?' + new URLSearchParams(options.params).toString()
      : '';
    const fullUrl = buildUrl(url) + searchParams;
    const data = await apiRequest<T>(fullUrl, { method: 'GET' }, token);
    return { data };
  },

  async post<T>(url: string, body?: any): Promise<{ data: T }> {
    const token = await getAccessToken();
    const fullUrl = buildUrl(url);
    const data = await apiRequest<T>(
      fullUrl,
      {
        method: 'POST',
        body: body ? JSON.stringify(body) : undefined,
      },
      token
    );
    return { data };
  },

  async put<T>(url: string, body?: any): Promise<{ data: T }> {
    const token = await getAccessToken();
    const fullUrl = buildUrl(url);
    const data = await apiRequest<T>(
      fullUrl,
      {
        method: 'PUT',
        body: body ? JSON.stringify(body) : undefined,
      },
      token
    );
    return { data };
  },

  async delete<T>(url: string): Promise<{ data: T }> {
    const token = await getAccessToken();
    const fullUrl = buildUrl(url);
    const data = await apiRequest<T>(fullUrl, { method: 'DELETE' }, token);
    return { data };
  },
};
